import { useState } from 'react';
import { api, ApiError } from '../api/client';
import { c, radius, shadow } from '../theme/tokens';

const PROVIDERS = [
  ['street', 'Street.co.uk'],
  ['vtuk', 'VTUK / iamproperty'],
  ['dezrez', 'Dezrez'],
  ['reapit', 'Reapit'],
  ['myporta', 'Use MyPorta listing data'],
  ['other', 'Other CRM'],
] as const;

export function CrmActivationModal({ branchId, onClose }: { branchId: string; onClose: () => void }) {
  const [provider, setProvider] = useState<(typeof PROVIDERS)[number][0] | ''>('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  /**
   * The server could not confirm the email reached Support.
   *
   * Distinct from `sent`, because the honest message is different: we must not
   * claim it was sent, and must not invite another attempt. Previously the
   * server returned this as a 202 with a `notified: 'unknown'` field that
   * nothing read, so the customer saw "has been sent to ModelProp Support"
   * (Codex on #61).
   */
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    if (!provider) { setError('Choose your property data source before sending the request.'); setBusy(false); return; }
    try { await api.requestCrmActivation(branchId, provider, notes); setSent(true); }
    catch (e) {
      if (e instanceof ApiError && e.deliveryUnknown) { setUnconfirmed(e.message); }
      else { setError(e instanceof ApiError ? e.message : 'Something went wrong sending your request.'); }
    }
    finally { setBusy(false); }
  };

  return <div role="dialog" aria-modal="true" aria-label="Link your CRM properties" style={{ position: 'fixed', inset: 0, zIndex: 80, display: 'grid', placeItems: 'center', padding: 20, background: 'rgba(11,18,32,.45)' }}>
    <section style={{ width: 'min(520px,100%)', padding: 30, borderRadius: 16, background: '#fff', boxShadow: shadow.drawer, display: 'flex', flexDirection: 'column', gap: 16 }}>
      {unconfirmed ? <>
        {/* No success tick and no retry button: both would be lies here. */}
        <div style={{ fontSize: 12, letterSpacing: '.12em', color: c.orange700, fontWeight: 700 }}>REQUEST SAVED</div>
        <h2 style={{ margin: 0, color: c.ink }}>We’re confirming this reached Support.</h2>
        <p style={{ margin: 0, color: c.ink2, lineHeight: 1.6 }}>{unconfirmed}</p>
        <button className="vp-reset" onClick={onClose} style={{ alignSelf: 'flex-start', padding: '10px 16px', borderRadius: radius.pill, background: c.ink, color: '#fff', cursor: 'pointer' }}>Done</button>
      </> : sent ? <>
        <div style={{ fontSize: 12, letterSpacing: '.12em', color: c.success, fontWeight: 700 }}>REQUEST RECEIVED</div>
        <h2 style={{ margin: 0, color: c.ink }}>We’ll be in touch to complete the connection.</h2>
        <p style={{ margin: 0, color: c.ink2, lineHeight: 1.6 }}>Your Pro CRM connection request has been sent to ModelProp Support.</p>
        <button className="vp-reset" onClick={onClose} style={{ alignSelf: 'flex-start', padding: '10px 16px', borderRadius: radius.pill, background: c.ink, color: '#fff', cursor: 'pointer' }}>Done</button>
      </> : <>
        <div style={{ fontSize: 12, letterSpacing: '.12em', color: c.sky, fontWeight: 700 }}>PRO ACTIVATION</div>
        <h2 style={{ margin: 0, color: c.ink }}>Link your property data</h2>
        <p style={{ margin: 0, color: c.ink2, lineHeight: 1.6 }}>Choose the source of your property data. ModelProp will confirm the connection with you. Do not enter passwords or API keys here.</p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7, color: c.ink, fontWeight: 600 }}>
          Property data source
          <select value={provider} onChange={e => setProvider(e.target.value as typeof provider)} style={{ padding: 11, borderRadius: 9, border: `1px solid ${c.line}`, background: '#fff', color: c.ink }}>
            <option value="">Choose a source…</option>
            {PROVIDERS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          {provider === 'myporta' ? <span style={{ fontSize: 12.5, fontWeight: 400, lineHeight: 1.5, color: c.orange700 }}>MyPorta listing data can be up to 24 hours behind, depending on your current upload profile.</span> : null}
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 7, color: c.ink, fontWeight: 600 }}>
          Anything Support should know? <span style={{ fontWeight: 400, color: c.ink3 }}>(optional)</span>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} maxLength={500} rows={3} style={{ padding: 11, borderRadius: 9, border: `1px solid ${c.line}`, resize: 'vertical' }} />
        </label>
        {error ? <div style={{ color: '#b42318', lineHeight: 1.45 }}>{error}</div> : null}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="vp-reset" onClick={onClose} disabled={busy} style={{ padding: '10px 16px', cursor: 'pointer' }}>Cancel</button>
          <button className="vp-reset" onClick={submit} disabled={busy} style={{ padding: '10px 16px', borderRadius: radius.pill, background: c.ink, color: '#fff', cursor: busy ? 'wait' : 'pointer' }}>{busy ? 'Sending…' : 'Send request'}</button>
        </div>
      </>}
    </section>
  </div>;
}
